/**
 * Executive tab — Season Actuals model (Chris, 2026-09-03).
 *
 * "This spreadsheet is only concerned with what IS, not what could be. It is
 * not modelling a future season, it is simply building a picture of a current
 * or past season."
 *
 * One season's DAILY SERVICES, running day by running day, from the sales the
 * tills recorded (the same rows the Timetable Studio's unoptimised side shows):
 *
 *   passengers     admissions — people, counted once, a multi-person ticket as its people (every Admission item's
 *                  quantity: paid tickets AND free re-visits, members, comps,
 *                  carers, babies), timed departures + untimed till sales.
 *   tickets sold   admissions with a non-zero net — paid tickets, refunds
 *                  netted off. Passengers minus tickets sold = free admissions.
 *   revenue        the day's daily-services net takings as the Studio counts
 *                  them: FARES on timed departures + OTHER TAKINGS with no
 *                  session (walk-up fares net of refunds, and the shop/catering
 *                  lines that share the daily-services booking type). Both
 *                  parts are shown so the composition is never hidden.
 *   services ran   the working timetable's passenger workings for the date's
 *                  colour where thebigplan holds one (2026 onwards) — a train
 *                  that ran empty still ran and still cost money — else the
 *                  timed departures that sold a seat (the sales evidence).
 *                  Each month says which.
 *   costs          the Studio's accounts-anchored cost model for THIS season's
 *                  own Management P&L (the planner's include ticks and
 *                  behaviours from the shared Studio defaults). FIXED = the
 *                  P&L's fixed lines for THAT month — the line runs all year, so
 *                  a month with no standard services is listed with its fixed
 *                  cost and nothing else (Chris, 2026-09-04); a running day
 *                  carries its share of its month's fixed. VARIABLE = the trains'
 *                  cost at the season's own £ per running day / working turn /
 *                  train-mile / passenger. Summed over the season this reconciles
 *                  to the included P&L lines.
 *   a service's profit  its fare revenue minus its VARIABLE cost (per turn,
 *                  per mile, per passenger) — the day-fixed block is the day's
 *                  cost, never one train's (law 1).
 *
 * Non-running days are omitted, and so is any day that sold no timed
 * departure (galas, events — the Studio's "no standard services" rule): only
 * days the railway ran standard services are pictured.
 *
 * Pure: every network read is injected, so the model runs headlessly.
 */
import { wttToDeps, type BaselineWtt, type BaselineWorking } from './studio/baseline'
import { parseRoute } from './studio/demand'
import { journeyMiles } from './studio/network'
import { canonicalDiagramId } from './studio/diagrams'
import { DEFAULT_NET } from './researchStudioModel'
import {
  ZERO_ACTIVITY, buildDayCostModel, buildSim, marginalRates, pnlDayVariable, pnlMonthFixed, pnlServiceVariable,
  type Activity, type CostConfig, type CostDriver, type StudioCostsResponse,
} from './studio/costLines'
import type { Network } from './studio/types'

// ---------------------------------------------------------------------------
//  The API payload (api/src/functions/execSeasonActuals.ts)
// ---------------------------------------------------------------------------
export interface SaSeasonRow { year_label: string; start_date: string; end_date: string }
export interface SaSlotRow { date: string; direction: string; dep_time: string; service: string | null; pax: number; net: number }
export interface SaUntimedRow { date: string; pax: number; net: number }
export interface SaItemRow { date: string; item: string; qty: number; net: number; soldQty: number; passenger: boolean
  /** People on the paid tickets (db/124: a Family is 4). Absent from an older API — falls back to soldQty. */
  soldPeople?: number }
export interface SaPayload {
  season: string
  from: string
  to: string
  seasons: SaSeasonRow[]
  slots: SaSlotRow[]
  untimed: SaUntimedRow[]
  items: SaItemRow[]
}

/** The reads the model needs, injected (the panel passes the real fetches). */
export interface SaSources {
  /** The working timetable covering a date (thebigplan) — null when none. */
  baselineFor: (isoDate: string) => Promise<BaselineWtt | null>
  /** The season's Management P&L cost lines (studio-costs) — null when none. */
  costsFor: (season: string) => Promise<StudioCostsResponse | null>
  /** The shared Studio cost configuration (include ticks / behaviours). */
  costConfig: () => Promise<CostConfig>
  /** Progress text for the panel. */
  onProgress?: (text: string) => void
}

// ---------------------------------------------------------------------------
//  The model
// ---------------------------------------------------------------------------
export interface SaService {
  time: string
  direction: string
  from: string
  to: string
  miles: number
  pax: number
  revenue: number
  /** Variable cost of this working: per turn + per mile × miles + per passenger × pax. */
  varCost: number | null
  profit: number | null
  /** 'wtt' = a timetabled working (thebigplan); 'sales' = a departure known only from its sales. */
  source: 'wtt' | 'sales'
  headcode: string
}
export interface SaDay {
  date: string
  /** 0 = Sunday … 6 = Saturday */
  dow: number
  colour: string | null
  servicesSource: 'wtt' | 'sales'
  services: SaService[]
  servicesRan: number
  /** Locomotive diagrams hauling passenger workings that day (WTT only). */
  locos: number | null
  pax: number
  ticketsSold: number
  fareRevenue: number
  otherTakings: number
  revenue: number
  /** This day's share of its month's fixed cost. */
  fixedCost: number | null
  /** The cost of the day's trains (per running day, turn, mile, passenger). */
  variableCost: number | null
  costs: number | null
  profit: number | null
  profitableServices: number | null
  lossServices: number | null
}
export interface SaTicket { item: string; qty: number; soldQty: number; net: number; passenger: boolean }
export interface SaMonth {
  key: string
  label: string
  calMonth: number
  year: number
  days: SaDay[]
  servicesRan: number
  maxLocos: number | null
  pax: number
  ticketsSold: number
  fareRevenue: number
  otherTakings: number
  revenue: number
  /** The month's FIXED cost from its own P&L — present even with no running days. */
  fixedCost: number | null
  variableCost: number | null
  costs: number | null
  profit: number | null
  tickets: SaTicket[]
  servicesSource: 'wtt' | 'sales' | 'mixed'
  timetableName: string | null
}
export interface SaCostBasis {
  loaded: boolean
  season: string
  /** The season's FIXED cost, all twelve months (the P&L's fixed lines). */
  fixedSeason: number
  rates: Record<CostDriver, number>
  includedLines: number
  note: string
}
export interface SaSeason {
  season: string
  from: string
  to: string
  months: SaMonth[]
  runningDays: number
  servicesRan: number
  maxLocos: number | null
  pax: number
  ticketsSold: number
  fareRevenue: number
  otherTakings: number
  revenue: number
  fixedCost: number | null
  variableCost: number | null
  costs: number | null
  profit: number | null
  tickets: SaTicket[]
  cost: SaCostBasis
  /** Provenance lines the workbook prints — nothing left to take on trust. */
  notes: string[]
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const STATION_NAMES = (net: Network) => net.stations.map((s) => s.name)

const addTicket = (acc: Map<string, SaTicket>, r: { item: string; qty: number; soldQty: number; net: number; passenger: boolean }) => {
  const t = acc.get(r.item) ?? { item: r.item, qty: 0, soldQty: 0, net: 0, passenger: r.passenger }
  t.qty += r.qty; t.soldQty += r.soldQty; t.net += r.net
  acc.set(r.item, t)
}
const ticketList = (acc: Map<string, SaTicket>): SaTicket[] => [...acc.values()].sort((a, b) => b.net - a.net || b.qty - a.qty)

/** Operating-year month index (March = 0 … February = 11) — the P&L's grain. */
const opIdx = (calMonth: number): number => (calMonth - 3 + 12) % 12

export async function buildSeasonActuals(payload: SaPayload, src: SaSources, net: Network = DEFAULT_NET, today: string = new Date().toISOString().slice(0, 10)): Promise<SaSeason> {
  const names = STATION_NAMES(net)
  const progress = (t: string) => src.onProgress?.(t)

  // ---- 1. The season's days from the tills ---------------------------------
  const slotsByDate = new Map<string, SaSlotRow[]>()
  for (const r of payload.slots) slotsByDate.set(r.date, [...(slotsByDate.get(r.date) ?? []), r])
  const untimedByDate = new Map(payload.untimed.map((u) => [u.date, u]))
  const itemsByDate = new Map<string, SaItemRow[]>()
  for (const r of payload.items) itemsByDate.set(r.date, [...(itemsByDate.get(r.date) ?? []), r])
  // A running day = a day that sold at least one seat on a timed departure.
  // (No timed sale = no standard services ran: a gala, an event, or nothing.)
  // What IS stops at the last COMPLETE day: an in-progress season holds
  // advance bookings for dates that have not run yet, and today is half-traded.
  const runningDates = [...slotsByDate.entries()]
    .filter(([d, rows]) => d < today && rows.reduce((t, r) => t + r.pax, 0) > 0)
    .map(([d]) => d)
    .sort()
  const futureBooked = [...slotsByDate.keys()].filter((d) => d >= today).length

  // ---- 2. Months, with the working timetable where thebigplan holds one ---
  // EVERY month of the season up to the last complete day is listed, running
  // or not — the line costs money in all of them (Chris, 2026-09-04).
  const monthKeys: string[] = []
  {
    // The last COMPLETE day is yesterday; a month that has not started is not listed.
    const y0 = new Date(`${today}T12:00:00Z`); y0.setUTCDate(y0.getUTCDate() - 1)
    const yesterday = y0.toISOString().slice(0, 10)
    const lastDay = payload.to < yesterday ? payload.to : yesterday
    for (let y = Number(payload.from.slice(0, 4)), m = Number(payload.from.slice(5, 7)); ; ) {
      const mk = `${y}-${String(m).padStart(2, '0')}`
      if (mk > lastDay.slice(0, 7)) break
      monthKeys.push(mk)
      m += 1; if (m > 12) { m = 1; y += 1 }
    }
  }
  const months: SaMonth[] = []
  const notes: string[] = []
  type DayShape = { services: SaService[]; source: 'wtt' | 'sales'; locos: number | null; colour: string | null }
  const shapes = new Map<string, DayShape>()
  const monthMeta = new Map<string, { timetableName: string | null; wttDays: number; salesDays: number }>()

  for (const mk of monthKeys) {
    const dates = runningDates.filter((d) => d.startsWith(mk))
    if (dates.length === 0) { monthMeta.set(mk, { timetableName: null, wttDays: 0, salesDays: 0 }); continue }
    progress(`Reading the ${MONTHS[Number(mk.slice(5, 7)) - 1]} ${mk.slice(0, 4)} timetable…`)
    let mw: BaselineWtt | null = null
    try { mw = await src.baselineFor(dates[0]) } catch { mw = null }
    const colourOf = new Map<string, string>()
    for (const d of mw?.days ?? []) if (d.run && d.date.startsWith(mk) && d.colour?.trim()) colourOf.set(d.date, d.colour.trim())
    const byPattern = new Map<string, BaselineWorking[]>()
    for (const w of mw?.workings ?? []) byPattern.set(w.pattern_code, [...(byPattern.get(w.pattern_code) ?? []), w])
    let wttDays = 0, salesDays = 0
    for (const date of dates) {
      const slots = slotsByDate.get(date) ?? []
      const colour = colourOf.get(date) ?? null
      const rows = colour ? byPattern.get(`TT-${colour.toUpperCase()}`) ?? [] : []
      const services: SaService[] = []
      let source: 'wtt' | 'sales' = 'sales'
      let locos: number | null = null
      if (rows.length > 0) {
        const obs = wttToDeps(rows, 'offpeak')
        if (obs.deps.length > 0) {
          source = 'wtt'
          const kept = new Set(rows.filter((w) => !obs.excluded.includes(w.headcode)).map((w) => w.headcode))
          const diagrams = new Set(rows.filter((w) => kept.has(w.headcode) && w.diagram && w.diagram !== 'TBA').map((w) => canonicalDiagramId(w.diagram!)))
          locos = diagrams.size
          const unmatched = [...slots]
          for (const dep of [...obs.deps].sort((a, b) => a.minutes - b.minutes)) {
            const i = unmatched.findIndex((s) => s.direction === dep.direction && s.dep_time === dep.time)
            const sale = i >= 0 ? unmatched.splice(i, 1)[0] : null
            services.push({
              time: dep.time, direction: dep.direction, from: names[dep.o] ?? String(dep.o), to: names[dep.d] ?? String(dep.d),
              miles: journeyMiles(net, dep.o, dep.d), pax: sale?.pax ?? 0, revenue: sale?.net ?? 0,
              varCost: null, profit: null, source: 'wtt', headcode: dep.id,
            })
          }
          // Sales on a departure the WTT does not show (a special, an extra)
          // are still services that ran — listed from their sales.
          for (const s of unmatched) services.push(saleService(net, names, s))
        }
      }
      if (source === 'sales') for (const s of slots) services.push(saleService(net, names, s))
      services.sort((a, b) => a.time.localeCompare(b.time))
      if (source === 'wtt') wttDays += 1; else salesDays += 1
      shapes.set(date, { services, source, locos, colour })
    }
    monthMeta.set(mk, { timetableName: mw?.timetable?.name ?? null, wttDays, salesDays })
  }

  // ---- 3. The season's own cost model (the Studio's, on its own P&L) --------
  progress('Loading the season’s Management P&L cost model…')
  const basis: Activity[] = Array.from({ length: 12 }, () => ({ ...ZERO_ACTIVITY }))
  for (const date of runningDates) {
    const sh = shapes.get(date)!
    const a = basis[opIdx(Number(date.slice(5, 7)))]
    a.runningDays += 1
    a.workings += sh.services.length
    a.trainMiles += sh.services.reduce((t, s) => t + s.miles, 0)
    a.passengers += (slotsByDate.get(date) ?? []).reduce((t, r) => t + r.pax, 0) + (untimedByDate.get(date)?.pax ?? 0)
  }
  let costsRes: StudioCostsResponse | null = null
  let costConfig: CostConfig = {}
  try { costsRes = await src.costsFor(payload.season) } catch { costsRes = null }
  try { costConfig = await src.costConfig() } catch { costConfig = {} }
  let cost: SaCostBasis = {
    loaded: false, season: payload.season, fixedSeason: 0,
    rates: { runningDays: 0, workings: 0, trainMiles: 0, passengers: 0 }, includedLines: 0,
    note: `No Management P&L is loaded for ${payload.season}, so no costs or profit are shown — the sales figures stand on their own.`,
  }
  let monthFixed: ((calMonth: number) => number) | null = null
  let dayVariable: ((services: number, pax: number, miles: number) => number) | null = null
  if (costsRes && costsRes.has_data) {
    const { sim, lines } = buildSim(costsRes, costConfig, [], basis, basis)
    const model = buildDayCostModel(lines, sim, basis, 24)
    const rates = marginalRates(lines, basis)
    const fixedSeason = model.fixedMonthCur.reduce((t, v) => t + v, 0)
    const included = lines.filter((l) => l.included).length
    cost = {
      loaded: true, season: payload.season, fixedSeason, rates, includedLines: included,
      note: `Costs are the Timetable Studio's accounts-anchored model on ${payload.season}'s OWN Management P&L (${included} included lines, the planner's include ticks and behaviours from the shared Studio defaults). FIXED = the P&L's fixed lines for THAT month (£${Math.round(fixedSeason).toLocaleString('en-GB')} across the season) — the line runs all year, so a month with no standard services is listed with its fixed cost and nothing else, and a running day carries its share of its month's fixed. VARIABLE = the trains' cost at this season's own rate — £${rates.workings.toFixed(2)} per working turn, £${rates.trainMiles.toFixed(2)} per train-mile, £${rates.passengers.toFixed(2)} per passenger, £${rates.runningDays.toFixed(2)} per running day. Summed over the season these reconcile to the included P&L lines. A service's profit is its fare revenue less its VARIABLE cost only; the fixed block is the month's cost, never one train's.`,
    }
    monthFixed = (calMonth) => pnlMonthFixed(model, 'cur', calMonth)
    dayVariable = (services, pax, miles) => pnlDayVariable(model, { services, carriedPerDay: pax, miles })
  }

  // ---- 4. Days, months, season --------------------------------------------
  const seasonTickets = new Map<string, SaTicket>()
  const tot = { days: 0, services: 0, pax: 0, sold: 0, fare: 0, other: 0, revenue: 0, fixed: 0, variable: 0, costs: 0, locos: null as number | null }
  for (const mk of monthKeys) {
    const yy = Number(mk.slice(0, 4)), calMonth = Number(mk.slice(5, 7))
    const monthTickets = new Map<string, SaTicket>()
    const days: SaDay[] = []
    let maxLocos: number | null = null
    const monthDates = runningDates.filter((d) => d.startsWith(mk))
    const mFixed = monthFixed ? monthFixed(calMonth) : null
    const fixedShare = mFixed == null ? null : mFixed / Math.max(1, monthDates.length)
    for (const date of monthDates) {
      const sh = shapes.get(date)!
      const slots = slotsByDate.get(date) ?? []
      const un = untimedByDate.get(date)
      const items = itemsByDate.get(date) ?? []
      const fareRevenue = slots.reduce((t, r) => t + r.net, 0)
      const otherTakings = un?.net ?? 0
      const pax = slots.reduce((t, r) => t + r.pax, 0) + (un?.pax ?? 0)
      // Paid passengers = the PEOPLE on passenger admissions with a non-zero net
      // (db/124: a Family ticket is 4; a dog ticket or a car-park hour is
      // takings, not a passenger — db/82). Passengers minus these = free.
      const ticketsSold = items.reduce((t, r) => t + (r.passenger ? (r.soldPeople ?? r.soldQty) : 0), 0)
      const miles = sh.services.reduce((t, s) => t + s.miles, 0)
      let fixedCost: number | null = null, variableCost: number | null = null, costs: number | null = null, profitable: number | null = null, lossMaking: number | null = null
      if (dayVariable && fixedShare != null) {
        fixedCost = fixedShare
        variableCost = dayVariable(sh.services.length, pax, miles)
        costs = fixedCost + variableCost
        profitable = 0; lossMaking = 0
        for (const s of sh.services) {
          s.varCost = pnlServiceVariable({ rate: cost.rates }, { miles: s.miles, pax: s.pax })
          s.profit = s.revenue - s.varCost
          if (s.profit > 0) profitable += 1; else lossMaking += 1
        }
      }
      const revenue = fareRevenue + otherTakings
      for (const r of items) { addTicket(monthTickets, r); addTicket(seasonTickets, r) }
      if (sh.locos != null) maxLocos = Math.max(maxLocos ?? 0, sh.locos)
      days.push({
        date, dow: new Date(`${date}T12:00:00Z`).getUTCDay(), colour: sh.colour, servicesSource: sh.source,
        services: sh.services, servicesRan: sh.services.length, locos: sh.locos,
        pax, ticketsSold, fareRevenue, otherTakings, revenue, fixedCost, variableCost, costs,
        profit: costs == null ? null : revenue - costs, profitableServices: profitable, lossServices: lossMaking,
      })
    }
    const meta = monthMeta.get(mk)!
    // The month's FIXED cost stands whether or not it ran (the line runs all year).
    const mVariable = dayVariable ? days.reduce((t, d) => t + (d.variableCost ?? 0), 0) : null
    const mCosts = mFixed == null || mVariable == null ? null : mFixed + mVariable
    const mRevenue = days.reduce((t, d) => t + d.revenue, 0)
    const m: SaMonth = {
      key: mk, label: `${MONTHS[calMonth - 1]} ${yy}`, calMonth, year: yy, days,
      servicesRan: days.reduce((t, d) => t + d.servicesRan, 0), maxLocos,
      pax: days.reduce((t, d) => t + d.pax, 0), ticketsSold: days.reduce((t, d) => t + d.ticketsSold, 0),
      fareRevenue: days.reduce((t, d) => t + d.fareRevenue, 0), otherTakings: days.reduce((t, d) => t + d.otherTakings, 0),
      revenue: mRevenue, fixedCost: mFixed, variableCost: mVariable, costs: mCosts, profit: mCosts == null ? null : mRevenue - mCosts,
      tickets: ticketList(monthTickets),
      servicesSource: meta.wttDays > 0 && meta.salesDays > 0 ? 'mixed' : meta.wttDays > 0 ? 'wtt' : 'sales',
      timetableName: meta.timetableName,
    }
    months.push(m)
    tot.days += days.length; tot.services += m.servicesRan; tot.pax += m.pax; tot.sold += m.ticketsSold
    tot.fare += m.fareRevenue; tot.other += m.otherTakings; tot.revenue += m.revenue
    tot.fixed += mFixed ?? 0; tot.variable += mVariable ?? 0; tot.costs += mCosts ?? 0
    if (maxLocos != null) tot.locos = Math.max(tot.locos ?? 0, maxLocos)
  }

  notes.push('Passengers = admissions (people, counted once): every passenger Admission item’s quantity on daily-services bookings — paid tickets and free re-visits, member visits, comps, carers and babies — on timed departures plus untimed till sales. Car parking, dog tickets, bicycles, memberships, platform tickets and vouchers are filed as Admission by MERAC but are NOT passengers: they are listed separately and their £ stays in the takings. A multi-person ticket counts the people on it — a Family ticket is 4, an Adult & One Child 2 — and its £ is shared between them. Paid passengers = the people on passenger admissions with a non-zero net (paid, refunds netted off). Passengers minus paid passengers = free admissions.')
  notes.push('Revenue = the day’s daily-services net takings as the Timetable Studio counts them: Fares (timed departures) + Other takings (sales with no session: walk-up fares net of refunds, and shop/catering lines that share the daily-services booking type). Both parts are shown.')
  const wttMonths = months.filter((m) => m.servicesSource !== 'sales').map((m) => m.label)
  const salesMonths = months.filter((m) => m.servicesSource !== 'wtt').map((m) => m.label)
  notes.push(`Services ran: ${wttMonths.length > 0 ? `${wttMonths.join(', ')} — the working timetable’s passenger workings for each date’s colour (thebigplan; a working that sold nothing is listed with 0 passengers); ` : ''}${salesMonths.length > 0 ? `${salesMonths.join(', ')} — the timed departures that sold a seat (thebigplan holds no working timetable for those dates), so Max locos is not known there.` : ''}`)
  notes.push(`Every month of the season up to the last complete day is listed — the line costs money in all of them — but only days the railway ran standard services appear as running days: a day that sold no timed departure (a gala, an event, or not running) is omitted, as are its till sales. The picture stops at the last complete day (${today} and later are not counted${futureBooked > 0 ? ` — ${futureBooked} future date${futureBooked === 1 ? '' : 's'} already hold advance bookings` : ''}).`)
  notes.push(cost.note)

  return {
    season: payload.season, from: payload.from, to: payload.to, months,
    runningDays: tot.days, servicesRan: tot.services, maxLocos: tot.locos, pax: tot.pax, ticketsSold: tot.sold,
    fareRevenue: tot.fare, otherTakings: tot.other, revenue: tot.revenue,
    fixedCost: monthFixed ? tot.fixed : null, variableCost: monthFixed ? tot.variable : null,
    costs: monthFixed ? tot.costs : null, profit: monthFixed ? tot.revenue - tot.costs : null,
    tickets: ticketList(seasonTickets), cost, notes,
  }
}

function saleService(net: Network, names: string[], s: SaSlotRow): SaService {
  const { o, d } = parseRoute(net, s.service, s.direction)
  return {
    time: s.dep_time, direction: s.direction, from: names[o] ?? String(o), to: names[d] ?? String(d),
    miles: journeyMiles(net, o, d), pax: s.pax, revenue: s.net, varCost: null, profit: null, source: 'sales', headcode: '',
  }
}
